
/**
 * Utility functions for handling referral codes in the application
 */

// Storage key for referral code
const REFERRAL_CODE_KEY = 'referral_code';

/**
 * Saves a referral code
 * @param referralCode The referral code to save
 * @returns boolean indicating if the code was saved (true) or not (false)
 */
export const saveReferralCode = (referralCode: string): boolean => {
  console.log(`[referralUtils] saveReferralCode called with code: ${referralCode}`);

  if (!referralCode) {
    console.log(`[referralUtils] No referral code provided, not saving`);
    return false;
  }

  console.log(`[referralUtils] Using storage key: ${REFERRAL_CODE_KEY}`);
  
  try {
    // Check if localStorage is available
    if (typeof localStorage === 'undefined') {
      console.error(`[referralUtils] localStorage is not available`);
      return false;
    }

    // Always save the new referral code, overwriting any previous one
    localStorage.setItem(REFERRAL_CODE_KEY, referralCode);
    console.log(`[referralUtils] Successfully saved referral code: ${referralCode}`);
    
    // Verify the code was saved correctly
    const savedCode = localStorage.getItem(REFERRAL_CODE_KEY);
    console.log(`[referralUtils] Verification - retrieved code: ${savedCode}`);
    
    return true;
  } catch (error) {
    console.error(`[referralUtils] Error saving referral code:`, error);
    return false;
  }
};

/**
 * Gets the saved referral code
 * @returns The saved referral code, or null if none exists
 */
export const getReferralCode = (): string | null => {
  console.log(`[referralUtils] getReferralCode called`);
  
  console.log(`[referralUtils] Using storage key: ${REFERRAL_CODE_KEY}`);
  
  try {
    // Check if localStorage is available
    if (typeof localStorage === 'undefined') {
      console.error(`[referralUtils] localStorage is not available`);
      return null;
    }
    
    const code = localStorage.getItem(REFERRAL_CODE_KEY);
    console.log(`[referralUtils] Retrieved referral code: ${code}`);
    return code;
  } catch (error) {
    console.error(`[referralUtils] Error retrieving referral code:`, error);
    return null;
  }
};

/**
 * Extracts the referral code from the current URL
 * @returns The referral code, or null if none exists
 */
export const extractReferralFromUrl = (): string | null => {
  console.log(`[referralUtils] extractReferralFromUrl called`);
  try {
    // Use window.location directly to get the current URL with search parameters
    const fullUrl = window.location.href;
    const searchParams = window.location.search;
    console.log('[referralUtils] Current URL:', fullUrl);
    console.log('[referralUtils] Current search parameters:', searchParams);
    
    // Create a URLSearchParams object from the current search string
    const urlParams = new URLSearchParams(searchParams);
    
    // Get the referral parameter
    const referral = urlParams.get('referral');
    if (referral) {
      console.log(`[referralUtils] Found referral code in URL parameters: ${referral}`);
      return referral;
    }
    console.log('[referralUtils] No referral code found in URL parameters');
    return null;
  } catch (error) {
    console.error('[referralUtils] Error extracting referral from URL:', error);
    return null;
  }
};

/**
 * Processes referral code from URL and stores it if found
 * @returns The referral code that was processed, or null if none found
 */
export const processReferralFromUrl = (): string | null => {
  console.log(`[referralUtils] processReferralFromUrl called`);
  
  const referralCode = extractReferralFromUrl();
  
  if (referralCode) {
    const saved = saveReferralCode(referralCode);
    if (saved) {
      console.log(`[referralUtils] Successfully processed and saved referral code: ${referralCode}`);
      return referralCode;
    } else {
      console.log(`[referralUtils] Failed to save referral code: ${referralCode}`);
    }
  }
  
  return null;
};

/**
 * Test function to validate localStorage is working
 */
export const testLocalStorage = (): boolean => {
  try {
    const testKey = 'test_storage_key';
    const testValue = 'test_value_' + Date.now();
    
    // Try to save to localStorage
    localStorage.setItem(testKey, testValue);
    
    // Try to retrieve from localStorage
    const retrieved = localStorage.getItem(testKey);
    
    // Clean up
    localStorage.removeItem(testKey);
    
    // Check if the retrieved value matches what we saved
    const success = retrieved === testValue;
    console.log(`LocalStorage test ${success ? 'PASSED' : 'FAILED'}`);
    return success;
  } catch (error) {
    console.error('LocalStorage test error:', error);
    return false;
  }
};

/**
 * Clears the stored referral code
 * For testing purposes
 */
export const clearReferralCode = (): void => {
  try {
    localStorage.removeItem(REFERRAL_CODE_KEY);
    console.log('Referral code cleared');
  } catch (error) {
    console.error('Error clearing referral code:', error);
  }
};
